import React, { useEffect } from "react";

import { styled } from "@catchoftheday/theme";

import { Box, BoxProps } from "../Box";
import { Flex } from "../Flex";
import { ArrowRightIcon } from "../Icon";
import { Link } from "../Link";
import { List, ListProps } from "../List";
import { ListItem, ListItemProps } from "../ListItem";
import { Span } from "../Text";

export type BreadcrumbProps = {
  /** Label to be displayed in the breadcrumb */
  label: string;
  /** Link for the breadcrumb to direct to */
  href: string;
  /** Set leaf to true if not rendering a link for the last item */
  leaf?: boolean;
};

export type BreadcrumbsProps = {
  /** Breadcrum items */
  breadcrumbs: BreadcrumbProps[];
  /** Container prop overwrites */
  containerProps?: BoxProps;
  /** List prop overwrites */
  listProps?: ListProps;
  /** Function to return listItemProps, takes index as an parameter */
  listItemProps?: (index: number) => ListItemProps;
};

const spanProps = {
  fontSize: "inherit",
  fontWeight: "inherit",
  lineHeight: "inherit",
};

const BreadcrumbContainer = styled(Box)`
  display: block;
  overflow: auto;
  max-width: 100%;
  white-space: nowrap;
`;

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  breadcrumbs,
  containerProps,
  listProps,
  listItemProps,
}) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);

  // The design requires the breadcrumb to scroll from the right on mobile
  useEffect(() => {
    const ref = scrollRef.current;
    if (!ref) return;
    const observer = new ResizeObserver(() => {
      ref.scrollLeft = ref.scrollWidth;
    });
    observer.observe(ref);
    return () => observer.unobserve(ref);
  }, [breadcrumbs]);

  return (
    <BreadcrumbContainer ref={scrollRef} {...containerProps} role="navigation">
      <List
        m="0"
        p="0"
        mb={2}
        itemType="http://schema.org/BreadcrumbList"
        itemScope
        {...listProps}
      >
        {breadcrumbs.map(({ label, href, leaf }, index) => (
          <ListItem
            fontSize="sm"
            key={label}
            itemType="http://schema.org/ListItem"
            itemScope
            itemProp="itemListElement"
            display="inline-block"
            mr={index === breadcrumbs.length - 1 ? 15 : 0}
            {...(listItemProps ? listItemProps(index) : {})}
          >
            <Flex alignItems="center">
              {leaf ? (
                <Span
                  itemProp="name"
                  color="typography.inactive"
                  {...spanProps}
                >
                  {label}
                </Span>
              ) : (
                <Link href={href} itemProp="item" hoverColor="secondary">
                  <Span itemProp="name" {...spanProps}>
                    {label}
                  </Span>
                </Link>
              )}
              {index !== breadcrumbs.length - 1 ? (
                <Flex mx={2}>
                  <ArrowRightIcon />
                </Flex>
              ) : null}
            </Flex>
            <meta itemProp="position" content={`${index + 1}`} />
          </ListItem>
        ))}
      </List>
    </BreadcrumbContainer>
  );
};

Breadcrumbs.displayName = "Breadcrumbs";

export default Breadcrumbs;
